
import React from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    ArrowUpRight,
    ShieldCheck,
    MapPin,
    Headphones,
    Sparkles,
} from "lucide-react";

const CTA_IMAGE =
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=2000&q=85";

export default function CallToAction() {
    const navigate = useNavigate();

    const handleExplore = () => {
        navigate("/hotels");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleWhyChooseUs = () => {
        const section = document.getElementById("why-choose-us");

        if (section) {
            section.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        } else {
            navigate("/#why-choose-us");
        }
    };

    return (
        <section
            id="call-to-action"
            className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-24"
        >
            <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">

                {/* MAIN CTA CONTAINER */}
                <div className="group relative isolate overflow-hidden rounded-[26px] bg-[#061D33] shadow-[0_25px_65px_rgba(6,29,51,0.18)] sm:rounded-[32px]">

                    {/* BACKGROUND IMAGE */}
                    <div className="absolute inset-0">
                        <img
                            src={CTA_IMAGE}
                            alt="Luxury hotel swimming pool"
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                        />

                        {/* DARK OVERLAY */}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#061D33]/95 via-[#061D33]/85 to-[#061D33]/45" />

                        <div className="absolute inset-0 bg-gradient-to-t from-[#061D33]/55 via-transparent to-transparent" />
                    </div>

                    {/* DECORATIVE ELEMENTS */}
                    <div className="pointer-events-none absolute -right-24 -top-24 h-[340px] w-[340px] rounded-full border border-[#E9B84F]/25" />

                    <div className="pointer-events-none absolute -right-12 -top-12 h-[250px] w-[250px] rounded-full border border-[#E9B84F]/15" />

                    <div className="pointer-events-none absolute bottom-0 left-0 h-[4px] w-40 bg-[#E9B84F]" />

                    {/* CONTENT */}
                    <div className="relative z-10 flex min-h-[460px] flex-col justify-center px-6 py-14 sm:min-h-[490px] sm:px-10 lg:min-h-[520px] lg:px-16 xl:px-20">

                        {/* EYEBROW */}
                        <div className="mb-6 flex items-center gap-3">
                            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E9B84F]/40 bg-[#E9B84F]/10">
                                <Sparkles
                                    size={17}
                                    className="text-[#E9B84F]"
                                />
                            </span>

                            <span className="text-[11px] font-bold uppercase tracking-[0.23em] text-[#E9B84F]">
                                Your Dubai Journey Begins Here
                            </span>
                        </div>

                        {/* HEADLINE */}
                        <h2 className="max-w-[780px] font-serif text-[35px] font-bold leading-[1.15] tracking-tight !text-white sm:text-[45px] lg:text-[56px] xl:text-[62px]">
                            Your Perfect Dubai Stay
                            <span className="block text-[#E9B84F]">
                                Is Just a Click Away.
                            </span>
                        </h2>

                        {/* DESCRIPTION */}
                        <p className="mt-6 max-w-[570px] text-[14px] leading-7 text-white/75 sm:text-[16px] sm:leading-8">
                            From iconic luxury hotels to comfortable city
                            stays, explore accommodation across Dubai
                            and find the right place for your next trip.
                        </p>

                        {/* BUTTONS */}
                        <div className="mt-9 flex flex-wrap items-center gap-4">

                            {/* PRIMARY BUTTON */}
                            <button
                                type="button"
                                onClick={handleExplore}
                                className="group/btn inline-flex h-[54px] items-center justify-center gap-3 rounded-xl bg-[#E9B84F] px-7 text-[12px] font-extrabold uppercase tracking-[0.07em] text-[#061D33] shadow-[0_8px_25px_rgba(233,184,79,0.22)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#F5C96C] hover:shadow-[0_12px_32px_rgba(233,184,79,0.35)]"
                            >
                                Explore Hotels

                                <ArrowRight
                                    size={17}
                                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                                />
                            </button>

                            {/* SECONDARY BUTTON */}
                            <button
                                type="button"
                                onClick={handleWhyChooseUs}
                                className="group/secondary inline-flex h-[54px] items-center justify-center gap-3 rounded-xl border border-white/40 bg-white/10 px-7 text-[12px] font-bold uppercase tracking-[0.06em] text-white backdrop-blur-sm transition-all duration-300 hover:border-white hover:bg-white/20"
                            >
                                Why Choose Us

                                <ArrowUpRight
                                    size={17}
                                    className="transition-transform duration-300 group-hover/secondary:translate-x-0.5 group-hover/secondary:-translate-y-0.5"
                                />
                            </button>
                        </div>

                        {/* TRUST FEATURES */}
                        <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-5 border-t border-white/20 pt-7">

                            <div className="flex items-center gap-2.5">
                                <ShieldCheck
                                    size={19}
                                    className="text-[#E9B84F]"
                                />

                                <span className="text-[12px] font-medium text-white/85">
                                    Secure Booking
                                </span>
                            </div>

                            <div className="flex items-center gap-2.5">
                                <MapPin
                                    size={19}
                                    className="text-[#E9B84F]"
                                />

                                <span className="text-[12px] font-medium text-white/85">
                                    Dubai Hotel Selection
                                </span>
                            </div>

                            <div className="flex items-center gap-2.5">
                                <Headphones
                                    size={19}
                                    className="text-[#E9B84F]"
                                />

                                <span className="text-[12px] font-medium text-white/85">
                                    Booking Assistance
                                </span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
}
